import React, { useState } from 'react'

import { F0Button, F0Dialog, F0Text } from '@factorialco/f0-react'
import { Input, Select, Stack, Textarea } from '@factorialco/f0-react/dist/experimental'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { CreateTicketDocument, TicketsConnectionDocument } from 'generated/resources/graphql'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useTeams } from 'modules/ticketing/hooks/useTeams'
import {
  getTicketPriorityLabel,
  getTicketPriorityOptions,
} from 'modules/ticketing/lib/ticketPriority'

export type TicketCreateDialogProps = {
  onSuccess: (ticketId: string) => void
  onClose: () => void
}

export const TicketCreateDialog: React.FC<TicketCreateDialogProps> = ({ onSuccess, onClose }) => {
  const { company } = useCurrentSession()
  const { teams } = useTeams()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('medium')
  const [assigneeTeamId, setAssigneeTeamId] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)

  const [performCreate, { loading: creating }] = useMutation(CreateTicketDocument, {
    extractorFn: (data) => data.ticketingMutations.createTicketingTicket,
  })

  const handleSubmit = async () => {
    const trimmedTitle = title.trim()
    if (!trimmedTitle) {
      setSubmitError(i18n.t('ticketing.inbox.create.title_required'))
      return
    }
    if (!assigneeTeamId) {
      setSubmitError(i18n.t('ticketing.inbox.create.team_required'))
      return
    }
    setSubmitError(null)

    const result = await performCreate({
      variables: {
        companyId: company.id,
        title: trimmedTitle,
        description: description.trim() || null,
        priority: priority || null,
        assigneeTeamId: assigneeTeamId,
      },
      refetchQueries: [TicketsConnectionDocument],
    })
    const handled = handleResult(result, () => {})
    if (handled.success && handled.data?.ticket != null) {
      onSuccess(handled.data.ticket.id)
    }
  }

  return (
    <F0Dialog
      isOpen
      module={{ id: 'inbox', label: i18n.t('ticketing.inbox.create.new_ticket') }}
      onClose={onClose}
    >
      <Stack paddingX="p-4" paddingY="p-4" gap="4" data-testid="ticket-create-form">
        <Input
          label={i18n.t('ticketing.inbox.columns.title')}
          value={title}
          onChange={(value) => setTitle(value)}
          placeholder={i18n.t('ticketing.inbox.create.title_placeholder')}
        />
        <Textarea
          label={i18n.t('ticketing.inbox.columns.description')}
          value={description}
          placeholder={i18n.t('ticketing.inbox.create.description_placeholder')}
          onChange={(value) => setDescription(value)}
          rows={4}
        />
        <Stack className="grid grid-cols-2 gap-4">
          <Select<string>
            label={i18n.t('ticketing.inbox.columns.priority')}
            value={priority}
            onChange={(value: string) => setPriority(value)}
            options={getTicketPriorityOptions().map((p) => ({
              value: p,
              label: getTicketPriorityLabel(p),
            }))}
          />
          <Select<string>
            label={i18n.t('ticketing.inbox.columns.assignee_team')}
            value={assigneeTeamId}
            onChange={(value: string) => setAssigneeTeamId(value)}
            options={teams.map((t) => ({
              value: String(t.value),
              label: t.label,
            }))}
          />
        </Stack>
        {submitError != null && <F0Text variant="small" content={submitError} />}
        <Stack gap="2" style={{ display: 'flex', flexDirection: 'row' }}>
          <F0Button
            label={i18n.t('ticketing.inbox.create.create_button')}
            onClick={handleSubmit}
            disabled={!title.trim() || !assigneeTeamId || creating}
          />
        </Stack>
      </Stack>
    </F0Dialog>
  )
}
